import api from './api';

/**
 * Site Status Service (Admin Panel)
 * مدیریت فلگ‌های فیچر و پنجره‌های به‌روزرسانی سایت
 *
 * نکته مهم: `api.get` در این پروژه پاسخ `{ status, data: X }` را خودش
 * unwrap می‌کند و فقط `X` را برمی‌گرداند. ولی `api.post/patch/put` کل
 * `response.data` را برمی‌گردانند، بنابراین مقدار واقعی در `.data` است.
 */

export type FeatureFlagKey =
  | 'wholesale'
  | 'offline_sales'
  | 'stories'
  | 'articles'
  | 'banners'
  | 'discount_codes'
  | 'tickets'
  | 'inspection'
  | 'chat'
  | 'notifications'
  | 'wallet'
  | 'withdrawals'
  | 'figapoints'
  | 'reviews'
  | 'orders'
  | 'cart'
  | 'kyc'
  | 'site_popups';

export interface FeatureFlagState {
  key: FeatureFlagKey;
  enabled: boolean;
  disabledMessageFa?: string;
  nameFa: string;
  descriptionFa: string;
  surfaces: string[];
}

export type MaintenanceGateKey =
  | 'payment.initiate'
  | 'order.create'
  | 'order.pay'
  | 'order.deliver'
  | 'order.cancel'
  | 'withdrawal.seller'
  | 'withdrawal.user'
  | 'wallet.charge'
  | 'wholesale.pool.create'
  | 'wholesale.claim'
  | 'wholesale.withdrawal'
  | 'offline.sale.create'
  | 'offline.sale.pay'
  | 'story.create'
  | 'inspection.submit'
  | 'banner.reserve';

export interface MaintenanceGateMeta {
  key: MaintenanceGateKey;
  nameFa: string;
  descriptionFa: string;
  featureKey?: FeatureFlagKey;
}

export interface MaintenanceWindow {
  _id: string;
  title: string;
  type: 'full' | 'partial';
  gateKeys: MaintenanceGateKey[];
  startsAt: string;
  endsAt: string;
  messageFa: string;
  active: boolean;
  allowAdminBypass: boolean;
  createdBy?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancellationReason?: string;
  createdAt: string;
  updatedAt: string;
}

// ── Store channel policies (نسخهٔ کافه‌بازار و سایر استورها) ──
// این لایه کاملاً از FeatureFlag سراسری جداست: فقط روی بیلدِ اپِ همان استور
// اثر می‌گذارد و رفتار وب را تغییر نمی‌دهد.

export type StoreChannel = 'direct' | 'bazaar' | 'myket' | 'playstore';

export type StoreChannelPolicyKey =
  | 'wallet_charge'
  | 'lottery'
  | 'promo_push'
  | 'store_social_links'
  | 'blog_menu'
  | 'vpn_ip_lookup'
  | 'direct_apk_download';

export interface StoreChannelPolicyState {
  key: StoreChannelPolicyKey;
  channel: StoreChannel;
  enabled: boolean;
  isOverridden: boolean;
  defaultEnabled: boolean;
  note?: string;
  lastChangedAt?: string;
  nameFa: string;
  descriptionFa: string;
  reasonFa: string;
  severity: 'blocker' | 'risk';
}

export interface StoreChannelPolicyResponse {
  channel: StoreChannel;
  channels: Array<{ key: StoreChannel; label: string }>;
  policies: StoreChannelPolicyState[];
}

class SiteStatusAdminService {
  // ── Feature Flags ──
  async listFlags(): Promise<FeatureFlagState[]> {
    // api.get unwraps { status, data: [...] } → just [...]
    const res = (await api.get('/admin/feature-flags')) as FeatureFlagState[];
    return Array.isArray(res) ? res : [];
  }

  async updateFlag(
    key: FeatureFlagKey,
    payload: { enabled?: boolean; disabledMessageFa?: string }
  ): Promise<FeatureFlagState> {
    // api.patch returns full { status, data } — extract .data
    const res = (await api.patch(`/admin/feature-flags/${key}`, payload)) as any;
    return res?.data ?? res;
  }

  async refreshFlagCache(): Promise<void> {
    await api.post('/admin/feature-flags/refresh-cache');
  }

  // ── Store Channel Policies ──
  async listStorePolicies(channel: StoreChannel): Promise<StoreChannelPolicyResponse> {
    // api.get unwraps { status, data } → just the data object
    const res = (await api.get('/admin/store-policies', {
      params: { channel },
    })) as StoreChannelPolicyResponse;
    return res;
  }

  async updateStorePolicy(
    channel: StoreChannel,
    key: StoreChannelPolicyKey,
    payload: { enabled: boolean; note?: string }
  ): Promise<StoreChannelPolicyState> {
    const res = (await api.patch(`/admin/store-policies/${channel}/${key}`, payload)) as any;
    return res?.data ?? res;
  }

  async resetStorePolicies(channel: StoreChannel): Promise<StoreChannelPolicyState[]> {
    const res = (await api.post(`/admin/store-policies/${channel}/reset`)) as any;
    return res?.data?.policies ?? [];
  }

  // ── Maintenance Windows ──
  async listGateCatalog(): Promise<MaintenanceGateMeta[]> {
    const res = (await api.get('/admin/maintenance-windows/catalog')) as MaintenanceGateMeta[];
    return Array.isArray(res) ? res : [];
  }

  async listWindows(filter: 'all' | 'active' | 'upcoming' | 'past' = 'all', page = 1, limit = 20) {
    const res: any = await api.get('/admin/maintenance-windows', {
      params: { filter, page, limit },
    });
    return res;
  }

  async createWindow(payload: {
    title: string;
    type: 'full' | 'partial';
    gateKeys?: MaintenanceGateKey[];
    startsAt: string;
    endsAt: string;
    messageFa: string;
    allowAdminBypass?: boolean;
    active?: boolean;
  }): Promise<MaintenanceWindow> {
    const res = (await api.post('/admin/maintenance-windows', payload)) as any;
    return res?.data ?? res;
  }

  async updateWindow(id: string, payload: Partial<MaintenanceWindow>): Promise<MaintenanceWindow> {
    const res = (await api.put(`/admin/maintenance-windows/${id}`, payload)) as any;
    return res?.data ?? res;
  }

  async cancelWindow(id: string, reason?: string): Promise<void> {
    await api.post(`/admin/maintenance-windows/${id}/cancel`, { reason });
  }
}

export default new SiteStatusAdminService();
